import { noteOwnPointer } from "@/features/collaboration/last-known-pointer";
import { getCookie, setCookie } from "@/shared";
import {
  useGetCurrentUserQuery,
  useGetUploadFileTokenMutation,
} from "@/shared/services";
import type { WholeStoryType } from "@config";
import { environment } from "@environment";
import { nanoid } from "@reduxjs/toolkit";
import { useCallback } from "react";
import { uploadFile } from "../rendering-provider/utils";

export const useUploadMetadata = () => {
  const { mutateAsync: getUploadFileToken } = useGetUploadFileTokenMutation();
  const { data: user } = useGetCurrentUserQuery();

  const handleUploadMetadata = useCallback(
    async (story: WholeStoryType, versionId: string | null = null) => {
      if (!user) throw new Error("User not found");
      const sessionId = handleSessionId({ storyId: story._id, versionId });
      const timestamp = new Date().getTime();
      const versionFragment = versionId ? `:${versionId}` : "";
      const filename = `${story.orgId}/metadata/${story._id}${versionFragment}/${sessionId}/${user._id}:${timestamp}`;
      const result = await getUploadFileToken({
        filename,
        contentType: "application/json",
        typeStory: false,
      });
      const url = `https://${environment.assetsBucket}/${result.url.fields.key}`;
      const blob = new Blob([JSON.stringify(story)], {
        type: "application/json",
      });
      await uploadFile(result.url, blob);
      // Remember that THIS browsing context minted this pointer, so a later
      // park can tell "I raced myself" from "a co-editor moved the head".
      // Registered here rather than at the call sites because every uploader
      // (collab autosave, the legacy updatePreviewAndMeta path, publish) goes
      // through this one function — a pointer any of them wrote is ours.
      noteOwnPointer({ storyId: story._id, versionId }, url);
      return { url, sessionId, userId: user._id };
    },
    [getUploadFileToken, user],
  );

  return handleUploadMetadata;
};

const handleSessionId = ({
  storyId,
  versionId,
}: {
  storyId: string;
  versionId: string | null;
}) => {
  const key = `session:${storyId}${versionId ? `:${versionId}` : ""}`;
  let sessionId = getCookie(key);
  if (!sessionId) sessionId = nanoid(20);
  setCookie({ name: key, value: sessionId, minutes: 30 });
  return sessionId;
};
